import { z } from 'zod';
import { HealthCheckReportSchema, PerformanceIssueDetailsSchema } from './health-check';

/**
 * Bug Report Types
 *
 * Comprehensive bug reporting system with maximum data collection
 * for effective debugging and issue tracking via Sentry.
 */

/**
 * System information automatically collected
 */
export const SystemInfoSchema = z.object({
  // Operating System
  platform: z.string(), // 'darwin', 'win32', 'linux'
  platformVersion: z.string(), // OS version
  arch: z.string(), // 'x64', 'arm64', etc.

  // Application
  appVersion: z.string(), // Package version
  electronVersion: z.string(),
  nodeVersion: z.string(),
  chromeVersion: z.string(),

  // Environment
  environment: z.enum(['development', 'production']),
  locale: z.string(),
  timezone: z.string(),

  // Memory & Performance
  totalMemoryMB: z.number(),
  usedMemoryMB: z.number(),
  uptime: z.number(), // seconds

  // Screen
  screenResolution: z.string(), // "1920x1080"
  windowSize: z.string(), // "800x600"
});

export type SystemInfo = z.infer<typeof SystemInfoSchema>;

/**
 * Application context at time of report
 */
export const AppContextSchema = z.object({
  currentView: z.string(), // 'chat', 'settings', etc.
  isOnline: z.boolean(),
  hasActiveSession: z.boolean(),
  lastError: z.string().optional(), // Last console error if any
  routeHistory: z.array(z.string()).optional(), // Last N routes visited
});

export type AppContext = z.infer<typeof AppContextSchema>;

/**
 * Console logs captured
 */
export const ConsoleLogsSchema = z.object({
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
  logs: z.array(z.string()).optional(),
});

export type ConsoleLogs = z.infer<typeof ConsoleLogsSchema>;

/**
 * Screenshot attachment
 */
export const ScreenshotAttachmentSchema = z.object({
  filename: z.string(),
  base64Data: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
});

export type ScreenshotAttachment = z.infer<typeof ScreenshotAttachmentSchema>;

/**
 * Log file attachment (text content from file logging)
 */
export const LogFileAttachmentSchema = z.object({
  filename: z.string(),
  content: z.string(), // Plain text log content
  sizeBytes: z.number(),
});

export type LogFileAttachment = z.infer<typeof LogFileAttachmentSchema>;

/**
 * Bug severity level
 */
export const BugSeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);

export type BugSeverity = z.infer<typeof BugSeveritySchema>;

/**
 * Bug category
 */
export const BugCategorySchema = z.enum([
  'crash',
  'ui',
  'performance', // General performance issues
  'performance-ai-slow', // AI responses are slow (auto-runs diagnostics)
  'data-loss',
  'authentication',
  'network',
  'feature',
  'other',
]);

export type BugCategory = z.infer<typeof BugCategorySchema>;

/**
 * Complete bug report submission
 */
export const SubmitBugReportInputSchema = z.object({
  // User-provided information
  title: z.string().min(5).max(200),
  description: z.string().min(10).max(5000),
  stepsToReproduce: z.string().optional(),
  expectedBehavior: z.string().optional(),
  actualBehavior: z.string().optional(),
  severity: BugSeveritySchema,
  category: BugCategorySchema,

  // Automatically collected data
  systemInfo: SystemInfoSchema,
  appContext: AppContextSchema,
  consoleLogs: ConsoleLogsSchema,

  // Optional attachments
  screenshot: ScreenshotAttachmentSchema.optional(),
  logFile: LogFileAttachmentSchema.optional(), // Automatically attached from file logging

  // Performance-specific data (auto-collected for performance-ai-slow category)
  performanceDetails: PerformanceIssueDetailsSchema.optional(),
  healthCheckReport: HealthCheckReportSchema.optional(),

  // Metadata
  timestamp: z.string(), // ISO timestamp
  sentrySessionUrl: z.string().optional(), // Link to Sentry session replay
});

export type SubmitBugReportInput = z.infer<typeof SubmitBugReportInputSchema>;

/**
 * Bug report submission response
 */
export const SubmitBugReportOutputSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  sentryEventId: z.string().optional(), // Sentry event ID for reference
  reportId: z.string().optional(), // Internal report ID if stored
});

export type SubmitBugReportOutput = z.infer<typeof SubmitBugReportOutputSchema>;

/**
 * UI display constants
 */
export const BUG_SEVERITY_LABELS: Record<BugSeverity, string> = {
  low: 'Low - Minor issue',
  medium: 'Medium - Noticeable problem',
  high: 'High - Major issue',
  critical: 'Critical - App unusable',
};

export const BUG_CATEGORY_LABELS: Record<BugCategory, string> = {
  crash: 'Application Crash',
  ui: 'UI/Visual Issue',
  performance: 'Performance/Lag',
  'performance-ai-slow': 'AI Response Slow (Auto-Diagnostics)',
  'data-loss': 'Data Loss',
  authentication: 'Login/Auth Issue',
  network: 'Network/Connectivity',
  feature: 'Feature Not Working',
  other: 'Other',
};
